import { useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { createDamageClient, damageAccountKey, type DamageClient } from '../client';
import { createDraft } from '../../browser-recovery/drafts';
import { emptyDamageDraft, validDamageDraft } from '../draft';
import type { DamageAccount } from '../types';
const clients = new Map<string, DamageClient>();
if (typeof window !== 'undefined') {
  window.addEventListener('storage', () => clients.forEach(c => c.refreshSaved()));
  window.addEventListener('beforeunload', e => { if ([...clients.values()].some(c => c.snapshot().pending)) { e.preventDefault(); e.returnValue = ''; } });
}
export function useDamageClient(base: string, account: DamageAccount, begin?: () => () => void) {
  const key = damageAccountKey(base, account), live = useRef(key); live.current = key;
  // Restore the account before child data-loading effects, including StrictMode remounts.
  useLayoutEffect(() => { live.current = key; return () => { live.current = ''; }; }, [key]);
  if (!clients.has(key)) clients.set(key, createDamageClient(base, account, begin));
  const client = clients.get(key)!; client.activate(() => live.current === key);
  const state = useSyncExternalStore(client.subscribe, client.snapshot);
  return { client, ...state, locked: state.busy || !!state.pending || state.blocked };
}
export function useDamageDraft(key: string, target: number | null) {
  const store = useMemo(() => createDraft(key, 'damage-v1', emptyDamageDraft(target), validDamageDraft), [key, target]);
  const state = useSyncExternalStore(store.subscribe, store.snapshot);
  return { ...store, ...state, locked: state.stale || state.changedElsewhere || state.unreadable || !!state.error };
}
